/**
 * ffmpeg is no longer inside the installer: Nova uses the copy on the computer,
 * or the person downloads it once (the engine fetches and checks it,
 * nova/tools.py). Until it is there, scans and analysis wait.
 *
 * Two places show it: a card at the top of the window while ffmpeg is missing
 * (Download, then a progress bar), and a row in Settings → Library that says
 * where ffmpeg was found.
 */

import { useCallback, useEffect, useState } from "react";
import { downloadFfmpeg, fetchTools, type ToolsReport } from "../api";
import { useApp } from "../core/app";
import { bus } from "../core/bus";

const mb = (bytes: number) => `${(bytes / 1048576).toFixed(0)} MB`;

export function useTools(offline: boolean): { report: ToolsReport | null; download: () => void; error: string | null } {
  const [report, setReport] = useState<ToolsReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const read = useCallback(() => void fetchTools().then((r) => r && setReport(r)), []);

  useEffect(() => {
    if (offline) return;
    read();
    const offs = [bus.on("tools.changed", read), bus.on("engine.connected", read), bus.on("engine.resync", read)];
    return () => offs.forEach((off) => off());
  }, [offline, read]);

  // Followed once a second while it downloads; nothing to follow otherwise.
  const busy = report?.progress.phase === "downloading" || report?.progress.phase === "checking";
  useEffect(() => {
    if (!busy) return;
    const timer = window.setInterval(read, 1000);
    return () => window.clearInterval(timer);
  }, [busy, read]);

  const download = useCallback(() => {
    setError(null);
    void downloadFfmpeg().then((r) => {
      if (r.error) setError(r.error);
      read();
    });
  }, [read]);

  return { report, download, error };
}

function Progress({ report, error, onDownload }: { report: ToolsReport; error: string | null; onDownload: () => void }) {
  const p = report.progress;
  const pct = p.total ? Math.min(100, Math.round((100 * p.downloaded) / p.total)) : null;
  const failed = p.phase === "failed" || !!error;
  return (
    <>
      {(p.phase === "downloading" || p.phase === "checking") && (
        <>
          <div className="nv-update-bar" aria-label="Download progress">
            <i style={{ width: `${p.phase === "checking" ? 100 : pct ?? 5}%` }} className={pct == null && p.phase === "downloading" ? "unknown" : ""} />
          </div>
          <span className="nv-dim tnum">
            {p.phase === "checking" ? "Checking the download…" : `${mb(p.downloaded)}${p.total ? ` of ${mb(p.total)}` : ""}`}
          </span>
        </>
      )}
      {failed && <span className="nv-ffmpeg-error">Did not download: {error ?? p.error ?? "unknown error"}</span>}
      {report.download && p.phase !== "downloading" && p.phase !== "checking" && (
        <div className="nv-update-actions">
          <button className="nv-btn primary" onClick={onDownload}>{failed ? "Try again" : "Download ffmpeg"}</button>
        </div>
      )}
      {!report.download && (
        <span className="nv-dim">
          Nova cannot fetch it on this computer. Install it yourself{report.hint ? <>: <code>{report.hint}</code></> : ""}, then
          reopen Nova.
        </span>
      )}
    </>
  );
}

/** The card at the top of the window while ffmpeg is missing. */
export function FfmpegNotice() {
  const app = useApp();
  const { report, download, error } = useTools(app.offline);
  // Above the first-run welcome so it can be answered there; Settings shows its own row.
  if (!report || report.ready || app.settingsPage) return null;
  return (
    <div className={`nv-update nv-ffmpeg-notice ${report.progress.phase === "failed" || error ? "failed" : ""}`} role="status" aria-live="polite">
      <div className="nv-update-head">
        <b>Nova needs ffmpeg to read your music.</b>
        <span className="nv-dim">
          It is a free program ({report.download ? "about 140 MB" : "not included with Nova"}). Adding folders and analysis wait until it is
          here.
        </span>
      </div>
      <Progress report={report} error={error} onDownload={download} />
    </div>
  );
}

/** The row in Settings → Library. */
export function FfmpegPanel() {
  const app = useApp();
  const { report, download, error } = useTools(app.offline);
  if (!report) return null;
  if (report.ready) {
    return (
      <div className="nv-update ready">
        <div className="nv-update-head">
          <b>ffmpeg</b>
          <span className="nv-dim">found: {report.found.ffmpeg}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={`nv-update ${report.progress.phase === "failed" || error ? "failed" : ""}`}>
      <div className="nv-update-head">
        <b>ffmpeg</b>
        <span className="nv-dim">is not on this computer yet. Nova needs it to read your music.</span>
      </div>
      <Progress report={report} error={error} onDownload={download} />
    </div>
  );
}
